import { Textbox } from "capitalrx-components-2.0/lib/forms";
import { Icon, Spinner } from "capitalrx-components-2.0/lib/general";
import { useCallback, useEffect, useRef, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { formularyDrugNameListAPI } from "@/api";
import { FormElementBaseProps } from "@/api/interfaces";

import styles from "./DrugSearchBox.module.scss";

const MINIMUM_SEARCH_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 500;
const NO_RESULTS_MESSAGE = "Drug not found. It may not be on the formulary.";

interface DrugNameRow {
  drug_name?: string | null;
  brand_name?: string | null;
  generic_name?: string | null;
  gpi?: string | null;
  representative_ndc?: string | null;
  tier?: string | null;
}

interface DrugNameOption {
  value: string;
  label: string;
  data: DrugNameRow;
}

const isNumericSearch = (term: string): boolean => /^\d+$/.test(term);

const DOSAGE_SUFFIX_REGEX = /\s+\d+(?:\.\d+)?\s*(mg|mcg|ml|unit|mbq|mci)\b.*$/i;

const drugDisplayName = (row: DrugNameRow): string => {
  const brandName = row.brand_name?.trim();
  if (brandName) {
    return brandName;
  }
  return row.drug_name ?? "";
};

const searchOptionName = (row: DrugNameRow): string => {
  const brandName = row.brand_name?.trim();
  if (brandName) {
    return brandName;
  }

  const displayName = drugDisplayName(row).trim();
  if (!displayName) {
    return "";
  }

  const collapsedName = displayName.replace(DOSAGE_SUFFIX_REGEX, "").trim();
  return collapsedName || displayName;
};

const buildOptions = (rows: DrugNameRow[], searchTerm: string): DrugNameOption[] => {
  if (isNumericSearch(searchTerm)) {
    return rows.map((row) => {
      const displayName = drugDisplayName(row);
      const ndc = row.representative_ndc ?? "";
      return { value: ndc, label: `${ndc} - ${displayName}`, data: row };
    });
  }

  const seen = new Set<string>();
  const options: DrugNameOption[] = [];
  for (const row of rows) {
    const displayName = searchOptionName(row);
    const dedupeKey = displayName.toLowerCase();

    if (!displayName || seen.has(dedupeKey)) {
      continue;
    }
    seen.add(dedupeKey);
    options.push({ value: displayName, label: displayName, data: row });
  }
  return options;
};

export const DrugSearchBox = ({
  name = "ndc",
  label = "Drug",
  disabled = false,
  testId,
  formularyId,
}: FormElementBaseProps & { formularyId?: number }) => {
  const { setValue } = useFormContext();
  const fieldValue = (useWatch({ name }) as string | null | undefined) ?? "";

  const [options, setOptions] = useState<DrugNameOption[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const runSearch = useCallback(
    async (searchTerm: string) => {
      if (formularyId === undefined) {
        setOptions([]);
        setHasSearched(false);
        return;
      }

      try {
        setIsLoading(true);

        const response = await formularyDrugNameListAPI({
          formulary_id: formularyId,
          search: searchTerm,
        });

        const rows: DrugNameRow[] = Array.isArray(response?.results) ? response.results : [];

        setOptions(buildOptions(rows, searchTerm));
      } catch (err) {
        console.error("Drug name search failed:", err);
        setOptions([]);
      } finally {
        setIsLoading(false);
        setHasSearched(true);
      }
    },
    [formularyId],
  );

  const handleChange = useCallback(
    (next: string | null) => {
      const value = next ?? "";
      setValue(name, value, { shouldDirty: true });

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      const searchTerm = value.trim();
      if (searchTerm.length < MINIMUM_SEARCH_LENGTH) {
        setIsOpen(false);
        setOptions([]);
        setHasSearched(false);
        return;
      }

      setIsOpen(true);
      setHasSearched(false);
      debounceRef.current = setTimeout(() => {
        void runSearch(searchTerm);
      }, SEARCH_DEBOUNCE_MS);
    },
    [name, runSearch, setValue],
  );

  const handleSelect = useCallback(
    (option: DrugNameOption) => {
      setValue(name, option.value, { shouldDirty: true });
      setIsOpen(false);
    },
    [name, setValue],
  );

  return (
    <div ref={containerRef} className={styles.DrugSearch}>
      <Textbox
        label={label}
        value={fieldValue}
        onChange={handleChange}
        disabled={disabled}
        testId={testId}
        placeholder="Search"
        autoComplete="off"
        prefix={<Icon name="search" />}
      />

      {isOpen && (
        <div className={styles.Menu}>
          {isLoading && (
            <div className={styles.MenuMessage}>
              <Spinner size="regular" />
            </div>
          )}

          {!isLoading && options.length > 0 && (
            <ul className={styles.MenuList}>
              {options.map((option) => (
                <li
                  key={option.label}
                  className={styles.MenuOption}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    handleSelect(option);
                  }}
                >
                  {option.label}
                </li>
              ))}
            </ul>
          )}

          {!isLoading && hasSearched && options.length === 0 && (
            <div className={styles.MenuMessage}>{NO_RESULTS_MESSAGE}</div>
          )}
        </div>
      )}
    </div>
  );
};
